import getRemovedPatients from "./getRemovedPatients"
import reassignPatients, { pickMostOverloadedPatient } from "./reassignPatients"

/**
 * Triggered when patients are marked Cancellation / No Show: rebalances the
 * floor by iteratively pulling one patient at a time from the most-overloaded
 * nurse onto any non-overloaded nurse. Stops when no nurse is overloaded
 * relative to the floor average.
 *
 * Unlike the previous version this does NOT restrict the target pool to
 * "nurses with cancellations" — anyone with capacity is a valid target.
 *
 * @param {{patientSchedule, nurseSchedule, chairSchedule}} schedule
 * @param {Object} settings
 * @param {boolean} sandbox
 * @param {boolean} isTomorrowMode
 * @returns {{reassignments: Array<{patientId, newNurseId}>, removedPatientsToConsider: Array}}
 */
const getAssignmentsForRemovedPatients = (schedule, settings, sandbox, isTomorrowMode) => {
	const removedPatientsToConsider = getRemovedPatients(schedule).filter(
		patient => patient.assignedNurse
	)
	if (removedPatientsToConsider.length === 0) {
		return { reassignments: [], removedPatientsToConsider: [] }
	}

	const workingSchedule = schedule.patientSchedule.map(p => ({ ...p }))
	const reassignments = []
	// Patients that have already been moved (so we don't ping-pong them) OR
	// that we tried to move but couldn't place anywhere.
	const lockedPatientIds = new Set()
	const candidateNurses = schedule.nurseSchedule
	const safety = workingSchedule.length

	for (let i = 0; i < safety; i++) {
		const next = pickMostOverloadedPatient({
			schedule,
			workingSchedule,
			excludePatientIds: lockedPatientIds
		})
		if (!next) break

		const result = reassignPatients({
			patientsToMove: [next.patient],
			candidateNurses: candidateNurses.filter(
				n => n.nurseId !== next.sourceNurse.nurseId
			),
			schedule: { ...schedule, patientSchedule: workingSchedule },
			settings,
			sandbox,
			isTomorrowMode,
			allowBumping: false
		})

		// Lock this patient either way — placed or unplaceable, we don't want
		// to revisit them in this rebalance session.
		lockedPatientIds.add(next.patient.patientId)

		if (result.length === 0) continue

		const a = result[0]
		reassignments.push(a)
		const wp = workingSchedule.find(p => p.patientId === a.patientId)
		if (wp) wp.assignedNurse = a.newNurseId
	}

	return { reassignments, removedPatientsToConsider }
}

export default getAssignmentsForRemovedPatients
